import { refreshWaiting } from './waiting'
import { supabase } from './supabase'
import type { PostOriginal } from './feed'
import type { PageContext } from './supportMedia'
import type { Me } from './me'
import type { HubLang } from './i18n'
import { LANE_WORDS_ES, type LaneWords } from './hubCopy'
import { intentRouting } from './wiki'

/**
 * The support lanes — HLP-3's client half. 085 built the roles, 089 built the
 * question, the read policy and the inbox.
 *
 * ── A LANE WITH NO HOLDER IS ABSENT, AND THE SERVER OWNS THAT ───────────────
 *
 * `support_lanes()` drops an unstaffed lane rather than flagging it, so there
 * is no client-side "is anybody there" check to write and there must not be
 * one: a second answer to that question would disagree with the first the day
 * somebody is granted or loses a role mid-session. What the client does with
 * the absence is refuse to offer the door, which is the same rule stated once
 * (085, and the drawer's own header).
 *
 * ── WHY THERE IS NO `answerSupportQuestion` HERE ────────────────────────────
 *
 * Because `addComment` already is it. `content_comment_read` is
 * `app.content_is_visible(content_id)`, so 089's read policy alone gives the
 * lane holder the right to reply and the asker the right to read it — and
 * nobody else either. 035: replying to a post and replying to a question are
 * the same ACT, and building "support replies" separately would be the
 * `content_creator` mistake a third time. `loadComments` and `addComment` in
 * `lib/wiki.ts` are the one home for both (standing rule 11).
 */

/** 085's three lane keys. They match `platform_help_entry.lane` and the part
 *  of each support role key after the dot; 089's suite asserts all three sets
 *  are equal, both ways. */
export type SupportLane = 'platform' | 'hr' | 'technical'

export const SUPPORT_LANES: readonly SupportLane[] = ['platform', 'hr', 'technical']

/** The prefix that MAKES a role a lane (085). There is no lane table: a lane
 *  IS a global role whose key begins with this, and the lane is the rest. */
export const SUPPORT_ROLE_PREFIX = 'support.'

/**
 * 337 (HLP-43, D3). What a private question IS: a ticket a person sent to a
 * lane they picked, or feedback - an idea or a suggestion, which goes to the
 * platform desk and nowhere else. Set by the server when it is sent and never
 * changed; the desk filters on it in the DATABASE, before the limit (rule 1).
 */
export type SupportKind = 'question' | 'feedback'
export const SUPPORT_KINDS: readonly SupportKind[] = ['question', 'feedback']

/**
 * 348 (HLP-43, D4 - David, 1 Nov). "How much is this in your way?" The ASKER's
 * word, said when the ticket is sent and never changed: the server refuses an
 * edit, by the asker or anybody else. Keys never change once written; the
 * words do (hubCopy HELP.priorities for the asker, PRIORITY_LABELS for the
 * desk). null is "not said": every ticket before 348, and feedback, which
 * never has one.
 *
 * The desk ORDER is the server's: inside Needs reply, can't work first, then
 * blocked, then the longest wait - before the limit (rule 1). Nothing here
 * sorts by it.
 */
export type AskerPriority = 'cant_work' | 'blocked' | 'annoying' | 'question'
export const ASKER_PRIORITIES: readonly AskerPriority[] = ['cant_work', 'blocked', 'annoying', 'question']

/** What the desk (an English staff screen, SHL-25) calls each one. */
export const PRIORITY_LABELS: Record<AskerPriority, string> = {
  cant_work: "Can't work",
  blocked: 'Blocked',
  annoying: 'Annoying',
  question: 'Question',
}

function priorityOf(v: unknown): AskerPriority | null {
  return typeof v === 'string' && (ASKER_PRIORITIES as readonly string[]).includes(v) ? (v as AskerPriority) : null
}

/**
 * 348. The priority BESIDE the reads whose return types are frozen
 * (support_inbox, handed_to_me, my_support_tickets, support_question) - 300's
 * post_bylines shape. The server answers only for threads the caller can
 * already read. Merged by id; the rows keep the order the server gave them.
 *
 * A failed read leaves the rows without a pill rather than failing the page:
 * the priority is a hint to the lane, and the list it decorates is the thing
 * the person came for.
 */
async function withPriorities<T extends { questionId: string }>(
  rows: T[],
): Promise<(T & { askerPriority: AskerPriority | null })[]> {
  if (rows.length === 0) return []
  let found = new Map<string, AskerPriority | null>()
  try {
    const { data, error } = await supabase.rpc('support_priorities', { p_ids: rows.map((r) => r.questionId) })
    if (error) throw error
    found = new Map(
      ((data ?? []) as { question_id: string; asker_priority: string | null }[]).map((r) => [
        r.question_id,
        priorityOf(r.asker_priority),
      ]),
    )
  } catch {
    // see above: no pill, never a failed list
  }
  return rows.map((r) => ({ ...r, askerPriority: found.get(r.questionId) ?? null }))
}

export type SupportLaneRow = {
  lane: SupportLane
  roleKey: string
  /** The role's display name, e.g. "Platform support". Not a person. */
  name: string
  /** How many people hold it. `support_lanes()` NEVER names them — it is
   *  DEFINER over `role_assignment` and must not become a second read path
   *  into the roster (ACC-16's objection, ACC-22's shape). */
  holders: number
}

/**
 * Which lanes are open. Empty is a real answer and means "no private door".
 *
 * `p_include_unstaffed` defaults to false, which is the drawer's case. An admin
 * screen that needs to STAFF an empty lane has to be able to see it, which is
 * what the parameter is for — and it is a REQUEST, not a permission (rule 20).
 */
export async function loadSupportLanes(includeUnstaffed = false): Promise<SupportLaneRow[]> {
  const { data, error } = await supabase.rpc('support_lanes', {
    p_include_unstaffed: includeUnstaffed,
  })
  if (error) throw new Error(error.message)
  return ((data ?? []) as Record<string, unknown>[]).map((r) => ({
    lane: r.lane as SupportLane,
    roleKey: r.role_key as string,
    name: r.name as string,
    holders: (r.holders as number) ?? 0,
  }))
}

/**
 * Which lanes THIS person answers, from the roles already loaded.
 *
 * Derived from `me.roles` rather than fetched, because a second round trip
 * would read the same three tables `loadMe` already read. It mirrors
 * `app.support_role_lane()` — the one home for that transformation is the
 * database, and this is a presentation convenience over data the session holds.
 * RLS is the gate: `support_inbox()` refuses a lane you do not hold whatever
 * this returns.
 */
export function myLanes(me: Me): SupportLane[] {
  const out = new Set<SupportLane>()
  for (const r of me.roles) {
    if (!r.key.startsWith(SUPPORT_ROLE_PREFIX)) continue
    const lane = r.key.slice(SUPPORT_ROLE_PREFIX.length) as SupportLane
    if (SUPPORT_LANES.includes(lane)) out.add(lane)
  }
  return SUPPORT_LANES.filter((l) => out.has(l))
}

/**
 * Send a private question to a lane.
 *
 * `oneSummary` travels WITH the verbatim question and never instead of it
 * (David, 17 Sep): a support team must never act on a paraphrase neither party
 * can check, so it is its own field rather than concatenated into `detail`, and
 * the asker can edit both before this is called.
 *
 * The lane is the ASKER'S CHOICE and is never re-derived (HLP-14). Nothing
 * infers it from the text, here or on the server.
 */
export async function createSupportQuestion(args: {
  lane: SupportLane
  body: string
  detail?: string
  oneSummary?: string
  /**
   * Where the asker was (116). The server keeps five keys and drops the rest;
   * the routing step shows the person these values before they send.
   */
  pageContext?: PageContext
  /** 348 (D4). Required: the seven-argument door refuses a ticket without it.
   *  Help's form has nothing picked until the person picks. */
  askerPriority: AskerPriority
}): Promise<string> {
  const { data, error } = await supabase.rpc('create_support_question', {
    p_lane: args.lane,
    p_body: args.body,
    p_detail: args.detail ?? null,
    p_one_summary: args.oneSummary ?? null,
    p_locale: null,
    p_page_context: args.pageContext ?? null,
    p_asker_priority: args.askerPriority,
  })
  if (error) throw new Error(error.message)
  return data as string
}

/**
 * 337 (D3). Feedback from Help > New > Feedback. Always the platform lane: the
 * server writes the lane, so nothing here can send feedback anywhere else. The
 * same 300-character headline rule as a question applies, so a longer note
 * keeps its first line as the headline and the whole text as the detail -
 * nothing typed is lost (`headlineAndDetail`).
 */
export async function sendSupportFeedback(args: { text: string }): Promise<string> {
  const { body, detail } = headlineAndDetail(args.text)
  const { data, error } = await supabase.rpc('send_support_feedback', {
    p_body: body,
    p_detail: detail,
    // No page details: the feedback form does not show them, and nothing goes
    // that the person was not shown first (116).
    p_page_context: null,
  })
  if (error) throw new Error(error.message)
  return data as string
}

/**
 * The headline a private question is filed under (300 characters at most, the
 * server's rule) and the detail that carries the rest. ONE home for the split:
 * One's one-tap send (v9.4) and Help's feedback both use it.
 */
export function headlineAndDetail(text: string): { body: string; detail: string | null } {
  const q = text.trim()
  if (q.length <= 300) return { body: q, detail: null }
  return { body: `${q.slice(0, 299).trimEnd()}…`, detail: q.slice(0, 2000) }
}

export type SupportQuestion = {
  questionId: string
  lane: SupportLane
  question: string
  detail: string | null
  oneSummary: string | null
  locale: string
  askedBy: string | null
  askedByPersonId: string
  askedAt: string
  /** The CURATOR's status (032), not the asker's verdict. The four are
   *  `wiki_question_status`; 'closed' was never one of them and this type said
   *  it was. */
  status: 'open' | 'answered' | 'declined' | 'duplicate'
  replies: number
  /**
   * HLP-22. The ASKER's answer to "did this answer your question?" — null until
   * they say.
   *
   * IT IS ON THE QUESTION AND NEVER ON A REPLY, which is the whole safety of
   * it: a reply has an author, so a verdict on a reply is a column that
   * aggregates by holder, and the per-person measurement this platform has
   * refused everywhere else (B22, B09 section 5, rule 8) would arrive here by
   * default rather than by decision. Nothing on a question can be grouped by
   * who answered it.
   *
   * `false` LEAVES THE THREAD OPEN. It is the signal this queue exists to
   * receive, not a failure state, and it must not be rendered as one — nobody
   * should hesitate to press it.
   */
  answeredTheAsker: boolean | null
  answeredTheAskerAt: string | null
  /** 118. Whose turn it is, from `app.support_turn()` — see `SupportTurn`. */
  turn: SupportTurn
  /** When it became that party's turn. Not when it was asked. */
  turnSince: string
  lastReplyBy: 'asker' | 'holder' | null
  lastReplyAt: string | null
  /** 117's triage, on the row — the machine's reading, painted as such. */
  triageCategory: string | null
  triageSeverity: 'low' | 'normal' | 'high' | null
  /** 117's handoff: the tracker id and the CACHED status last seen. */
  trackerIssueId: string | null
  trackerStatus: string | null
  /** 116: how many pictures came with it. */
  pictures: number
  /** 119: the one named person this question was handed to, if any. */
  assignedTo: string | null
  assignedToPersonId: string | null
  assignedAt: string | null
  /** 123. 'machine' when `question`/`detail` are One's rendering in YOUR
   *  language rather than the asker's own words. The server resolves for the
   *  reader (app.reader_locale()); nothing here picks a locale. */
  translationStatus: 'machine' | null
  /** 337. Absent where the read does not say (handed_to_me). */
  kind?: SupportKind | null
  /** 348. The asker's "how much is this in your way?"; null = not said. */
  askerPriority?: AskerPriority | null
}

/* ── 118: WHOSE TURN IT IS ────────────────────────────────────────────────────
 *
 * DERIVED BY THE DATABASE, NEVER HERE. `app.support_turn()` is the one home
 * (rule 11): the inbox, the asker's own list and the thread head all read it,
 * so both parties describe the same thread the same way from opposite sides.
 * A `turnOf()` in this file would be a second answer that disagrees exactly at
 * the boundary — a verdict and a reply in the same minute.
 *
 *   holder    waiting on the lane: no reply yet, the asker replied last, or
 *             the asker said "not yet" after the lane's last reply
 *   asker     waiting on the asker: the lane replied last
 *   closed    174: the lane closed it and nothing has been said since. Not a
 *             confirmation — the asker is asked, and any reply or "not yet"
 *             reopens it, derived on the server like everything else here
 *   answered  the asker said yes (090) — the only CONFIRMED state
 *
 * The sort and the turn FILTER run before the limit on the server (standing
 * rule 1); this file only names them.
 */
export type SupportTurn = 'holder' | 'asker' | 'closed' | 'answered'
export const SUPPORT_TURNS: readonly SupportTurn[] = ['holder', 'asker', 'closed', 'answered']

/** What the LANE calls each turn. English on purpose: the queue is a staff
 *  screen (SHL-25). The asker's side of the same words is in hubCopy's
 *  SUPPORT.turn, because that screen is theirs and speaks their language.
 *  174 (David, 11 Oct): "closed" and "confirmed" are told apart everywhere, so
 *  a clean queue cannot hide a question nobody confirmed (090's objection). */
export const TURN_LABELS: Record<SupportTurn, string> = {
  holder: 'Needs reply',
  asker: 'Waiting on asker',
  closed: 'Closed · not confirmed',
  answered: 'Confirmed by asker',
}

/* ── 174: THE DESK'S THREE VIEWS ──────────────────────────────────────────────
 *
 * Four turns, three views: the lane's Closed view is both "closed by the lane"
 * and "confirmed by the asker", newest first. `done` is a FILTER the server
 * understands (`support_inbox(p_turn => 'done')`), applied before the limit —
 * never two reads merged here (standing rule 1).
 */
export type DeskView = 'holder' | 'asker' | 'done'
export const DESK_VIEWS: readonly DeskView[] = ['holder', 'asker', 'done']
export const DESK_VIEW_LABELS: Record<DeskView, string> = {
  holder: 'Needs reply',
  asker: 'Waiting on asker',
  done: 'Closed',
}
/** A view's count, from the four the server returns. Arithmetic over the
 *  server's numbers, not a second derivation of any turn. */
export function viewCount(counts: Record<SupportTurn, number>, view: DeskView): number {
  return view === 'done' ? counts.closed + counts.answered : counts[view]
}

/**
 * How long since `iso`, as a duration rather than a relative time — "26 h",
 * not "26 hours ago" — because the queue says "waiting 26 h" and the age is
 * the thing being compared down the list. Tabular on screen.
 */
export function durationSince(iso: string, now: number = Date.now()): string {
  const ms = now - new Date(iso).getTime()
  if (!Number.isFinite(ms) || ms < 0) return '0 min'
  const min = Math.round(ms / 60_000)
  if (min < 60) return `${min} min`
  const h = Math.round(ms / 3_600_000)
  if (h < 36) return `${h} h`
  return `${Math.round(ms / 86_400_000)} d`
}

export type SupportInboxPage = {
  rows: SupportQuestion[]
  /** How many matched, not how many came back. REC-54: a page shown as the
   *  whole list is the same false claim as an empty list shown as the world. */
  totalMatches: number
}

/**
 * The queue. `lane` omitted means every lane you hold.
 *
 * A LANE YOU DO NOT HOLD IS A REFUSAL, NOT AN EMPTY LIST (rule 12), and so is
 * holding none — both come back as a thrown error with a sentence, because an
 * empty platform inbox reads as "nobody has asked anything", which is a claim
 * about the company rather than about your own role.
 */
export async function loadSupportInbox(opts: {
  lane?: SupportLane | null
  /** 118. Filtered on the SERVER, before the limit. 174: `done` is the desk's
   *  Closed view (closed or confirmed), also the server's. */
  turn?: SupportTurn | 'done' | null
  /** 118. Matched on the server as characters, not a pattern. */
  search?: string | null
  /** 337. Feedback or tickets only; null is both. Filtered on the SERVER. */
  kind?: SupportKind | null
  limit?: number
} = {}): Promise<SupportInboxPage> {
  // 337. Always the five-argument form (p_kind present, null for both), so
  // the row carries its kind whatever the filter.
  const { data, error } = await supabase.rpc('support_inbox', {
    p_lane: opts.lane ?? null,
    p_limit: opts.limit ?? 25,
    p_turn: opts.turn ?? null,
    p_search: opts.search?.trim() || null,
    p_kind: opts.kind ?? null,
  })
  if (error) throw new Error(error.message)
  const raw = (data ?? []) as Record<string, unknown>[]
  return {
    rows: await withPriorities(raw.map(rowToSupportQuestion)),
    // Every row carries it (088's window), so the first is as good as any —
    // and zero rows means zero matches, which is the only case where the
    // absence and the answer agree.
    totalMatches: (raw[0]?.total_matches as number) ?? 0,
  }
}

/** One mapper for the two reads that share a row shape (support_inbox,
 *  handed_to_me) — a second copy is a second answer to what a column means. */
function rowToSupportQuestion(r: Record<string, unknown>): SupportQuestion {
  return {
    questionId: r.question_id as string,
    lane: r.lane as SupportLane,
    question: r.question as string,
    detail: (r.detail as string) ?? null,
    oneSummary: (r.one_summary as string) ?? null,
    locale: r.locale as string,
    askedBy: (r.asked_by as string) ?? null,
    askedByPersonId: r.asked_by_person_id as string,
    askedAt: r.asked_at as string,
    status: r.status as SupportQuestion['status'],
    replies: (r.replies as number) ?? 0,
    answeredTheAsker: (r.answered_the_asker as boolean) ?? null,
    answeredTheAskerAt: (r.answered_the_asker_at as string) ?? null,
    turn: r.turn as SupportTurn,
    turnSince: r.turn_since as string,
    lastReplyBy: (r.last_reply_by as 'asker' | 'holder') ?? null,
    lastReplyAt: (r.last_reply_at as string) ?? null,
    triageCategory: (r.triage_category as string) ?? null,
    triageSeverity: (r.triage_severity as SupportQuestion['triageSeverity']) ?? null,
    trackerIssueId: (r.tracker_issue_id as string) ?? null,
    trackerStatus: (r.tracker_status as string) ?? null,
    pictures: (r.pictures as number) ?? 0,
    assignedTo: (r.assigned_to as string) ?? null,
    assignedToPersonId: (r.assigned_to_person_id as string) ?? null,
    assignedAt: (r.assigned_at as string) ?? null,
    translationStatus: r.translation_status === 'machine' ? 'machine' : null,
    kind: r.kind === 'feedback' ? 'feedback' : r.kind === 'question' ? 'question' : null,
  }
}

/* ── 119: A QUESTION HANDED TO ONE NAMED PERSON ─────────────────────────────
 *
 * Not a role. `assigned_person_id` on the question gives one colleague — who
 * may hold no lane at all — THIS thread: the question, its pictures, the
 * replies, and the staff column for it. Nothing else in the lane. The lane
 * holder hands it (with a reason, which becomes an internal note); the lane
 * or the person can hand it back. The asker is told who has it, because
 * private means private to a NAMED audience (16 Sep).
 *
 * The server checks all of it (rule 21). These are the two calls.
 */

/** Hand a private question to a colleague, or back (personId null). The
 *  reason is required when handing; it is the record of why (HLP-4). */
export async function handSupportQuestion(
  questionId: string,
  personId: string | null,
  note?: string,
): Promise<void> {
  const { error } = await supabase.rpc('hand_support_question', {
    p_content_id: questionId,
    p_person_id: personId,
    p_note: note?.trim() || null,
  })
  if (error) throw new Error(error.message)
}

/* ── 121: A QUESTION MOVED TO ANOTHER LANE (HLP-4) ──────────────────────────
 *
 * The asker picks the lane (089) and routing is never right every time, so a
 * holder of the lane a question is IN moves it, with a reason. On the server
 * the reason becomes an internal note, the move is recorded twice — once with
 * nobody's name and once with no text (037's split) — and the asker and the
 * new lane are told. Nothing here suggests a lane and nothing auto-routes:
 * person picks, system watches, suggestion last.
 *
 * The server refuses (rule 21): anybody but a holder of the current lane, the
 * same lane, a lane nobody holds, and — while HLP-20 holds — the HR lane.
 */
export async function moveSupportQuestion(questionId: string, lane: SupportLane, reason: string): Promise<void> {
  const { error } = await supabase.rpc('move_support_question', {
    p_content_id: questionId,
    p_lane: lane,
    p_reason: reason.trim(),
  })
  if (error) throw new Error(error.message)
}

/**
 * Where a question in `current` may be moved. From the OPEN lanes the server
 * listed (`support_lanes()` drops a lane nobody holds) and the drawer's own
 * routing (`intentRouting`, whose `hold` IS the HR ruling of 18 Sep — one
 * home, so the desk offers exactly the doors the drawer does and turning HR on
 * is still deleting one line). `undefined` — not loaded yet — means nowhere,
 * the drawer's posture: a door rendered while the fetch is in flight reaches
 * nobody.
 */
export function moveTargets(current: SupportLane, openLanes: readonly SupportLane[] | undefined): SupportLane[] {
  if (!openLanes) return []
  return SUPPORT_LANES.filter((l) => l !== current && intentRouting(l, openLanes) === 'lane')
}

/**
 * The private questions handed to ME, whatever lane they sit in — the same
 * row shape as the inbox so the desk renders them with one row component.
 * Self-only by construction, so an empty list is a true statement about you
 * (the MyQuestions posture), never the inbox's refusal.
 */
export async function loadHandedToMe(limit = 25): Promise<SupportInboxPage> {
  const { data, error } = await supabase.rpc('handed_to_me', { p_limit: limit })
  if (error) throw new Error(error.message)
  const raw = (data ?? []) as Record<string, unknown>[]
  return {
    rows: await withPriorities(raw.map(rowToSupportQuestion)),
    totalMatches: (raw[0]?.total_matches as number) ?? 0,
  }
}

/**
 * 118. How many are waiting on you, on them, closed (174) and answered — across the lanes
 * you hold, or one lane. The SAME lane check and the same refusals as the
 * inbox: an employee holding no lane gets an error, never three zeros, because
 * "0 waiting" is a claim about the company (rule 12). All four keys always
 * come back, so a chip never has to decide what a missing row means.
 *
 * Per turn and per lane only. Never per person (B22, rule 8).
 */
export async function loadSupportInboxCounts(
  lane?: SupportLane | null,
  /** 337. The same kind filter as the inbox, so the views count what they show. */
  kind?: SupportKind | null,
): Promise<Record<SupportTurn, number>> {
  const { data, error } = await supabase.rpc('support_inbox_counts', { p_lane: lane ?? null, p_kind: kind ?? null })
  if (error) throw new Error(error.message)
  const out: Record<SupportTurn, number> = { holder: 0, asker: 0, closed: 0, answered: 0 }
  for (const r of (data ?? []) as { turn: SupportTurn; n: number }[]) {
    if (r.turn in out) out[r.turn] = r.n ?? 0
  }
  return out
}

/**
 * What a lane is CALLED where a person reads it, in their own terms rather than
 * the team's. One home for the wording (rule 11) — the drawer's intent rows,
 * the inbox's lane chips and the inbox's heading all read from here.
 *
 * `support.technical` MEANS THE WORK and never IT (085's rename). A broken
 * button is `support.platform`, and the word must not drift back, which is why
 * the description says what it is about rather than naming a department.
 */
export const LANE_LABELS: Record<SupportLane, { label: string; about: string }> = {
  platform: {
    label: 'Platform support',
    about: 'The hub itself — something broken, confusing or missing, and where ideas go.',
  },
  hr: {
    label: 'HR',
    about: 'Pay, leave, contracts and benefits.',
  },
  technical: {
    label: 'Technical expert',
    about: 'Field data, testing, equipment, client deliverables. The work, not the hub.',
  },
}

export function laneLabel(lane: SupportLane): string {
  return LANE_LABELS[lane]?.label ?? lane
}

/**
 * How the drawer speaks about a lane, in the reader's language (SHL-25).
 * English is LANE_LABELS above and derives `to`/`queue` exactly as the drawer
 * always did — the label, lowercased — so no English sentence moved. Spanish
 * carries its own, because it needs articles the label does not.
 */
export function laneWords(lane: SupportLane, lang: HubLang = 'en'): LaneWords {
  if (lang === 'es') return LANE_WORDS_ES[lane]
  const label = laneLabel(lane)
  return { label, about: LANE_LABELS[lane]?.about ?? '', to: label.toLowerCase(), queue: label.toLowerCase() }
}

/* ── HLP-22: the loop closes ─────────────────────────────────────────────────
 *
 * Before 090 a private question could be answered and the asker never find out,
 * and a lane like that is a suggestion box. Three things were missing and none
 * of them was a write path: a NOTIFICATION that links, a SCREEN to land on, and
 * a way for the asker to say whether it worked.
 *
 * STILL NO SECOND REPLY MECHANISM. `addComment` in lib/wiki.ts is how both
 * parties speak, in both directions — 089's finding (035, rule 6) and it is
 * unchanged. What 090 changed is who gets TOLD.
 */

/** One row of a person's own private questions. Not `SupportQuestion`: this
 *  reader deliberately returns no asker name, because the asker is you. */
export type MySupportQuestion = {
  questionId: string
  lane: SupportLane
  question: string
  detail: string | null
  oneSummary: string | null
  locale: string
  askedAt: string
  status: SupportQuestion['status']
  replies: number
  answeredTheAsker: boolean | null
  answeredTheAskerAt: string | null
  /** 118 — the same turn the lane sees, from the one home. */
  turn: SupportTurn
  turnSince: string
  lastReplyBy: 'asker' | 'holder' | null
  lastReplyAt: string | null
  /** 123. See SupportQuestion.translationStatus. For the asker this is their
   *  own words unless they changed language after asking. */
  translationStatus: 'machine' | null
  /** 337. A ticket or feedback - Help > Your tickets names which. Absent on
   *  the thread head, which does not say yet. */
  kind?: SupportKind
  /** 348. What you said when you sent it; recorded, not editable. */
  askerPriority?: AskerPriority | null
}

export type MySupportQuestionsPage = {
  rows: MySupportQuestion[]
  /** REC-54: how many matched, not how many came back. */
  totalMatches: number
}

/**
 * A person's own private questions.
 *
 * Empty is a TRUE answer here and is not rule 12's case: "you have asked
 * nothing" is a fact about you, not a claim about the company, which is what
 * made an empty `support_inbox` a lie. So this returns a list and the screen
 * says the honest thing about it.
 */
export async function loadMySupportQuestions(limit = 25): Promise<MySupportQuestionsPage> {
  // 337. my_support_tickets is the body (my_support_questions delegates to it)
  // and the only one that says the kind.
  const { data, error } = await supabase.rpc('my_support_tickets', { p_limit: limit })
  if (error) throw new Error(error.message)
  const raw = (data ?? []) as Record<string, unknown>[]
  return {
    rows: await withPriorities(raw.map((r): MySupportQuestion => ({
      questionId: r.question_id as string,
      lane: r.lane as SupportLane,
      question: r.question as string,
      detail: (r.detail as string) ?? null,
      oneSummary: (r.one_summary as string) ?? null,
      locale: r.locale as string,
      askedAt: r.asked_at as string,
      status: r.status as SupportQuestion['status'],
      replies: (r.replies as number) ?? 0,
      answeredTheAsker: (r.answered_the_asker as boolean) ?? null,
      answeredTheAskerAt: (r.answered_the_asker_at as string) ?? null,
      turn: r.turn as SupportTurn,
      turnSince: r.turn_since as string,
      lastReplyBy: (r.last_reply_by as 'asker' | 'holder') ?? null,
      lastReplyAt: (r.last_reply_at as string) ?? null,
      translationStatus: r.translation_status === 'machine' ? 'machine' : null,
      kind: r.kind === 'feedback' ? 'feedback' : 'question',
    }))),
    totalMatches: (raw[0]?.total_matches as number) ?? 0,
  }
}

/** One thread's question row, for whichever of its two readers opened it. */
export type SupportThreadHead = MySupportQuestion & {
  askedBy: string | null
  askedByPersonId: string
  /** Which side of the thread you are on. The SERVER decides it — there is no
   *  client-side "am I the author" test, which would be a second answer. */
  iAsked: boolean
  /** 116. Where the asker was, and their pictures. Both come from the one
   *  read of the thread, so its two readers see the same thing. */
  pageContext: Partial<PageContext> | null
  pictures: { path: string; width: number; height: number }[]
  /** 119. Who it is handed to, and whether that is you. Both from the SERVER. */
  assignedTo: string | null
  assignedToPersonId: string | null
  assignedAt: string | null
  iAmAssignee: boolean
}

/**
 * Open one private thread. `#/support/:id` reads this.
 *
 * A thread you cannot see REFUSES rather than coming back empty (rule 12), and
 * the refusal is the SAME SENTENCE for "no such id" as for "not yours" — two
 * different messages would make this an oracle for whether any given uuid is a
 * private question, which is most of what a private question is hiding.
 */
export async function loadSupportThread(questionId: string): Promise<SupportThreadHead> {
  const { data, error } = await supabase.rpc('support_question', { p_content_id: questionId })
  if (error) throw new Error(error.message)
  const r = ((data ?? []) as Record<string, unknown>[])[0]
  if (!r) throw new Error('no such private question, or it is not yours to read')
  const [pri] = await withPriorities([{ questionId: r.question_id as string }])
  return {
    questionId: r.question_id as string,
    lane: r.lane as SupportLane,
    question: r.question as string,
    detail: (r.detail as string) ?? null,
    oneSummary: (r.one_summary as string) ?? null,
    locale: r.locale as string,
    askedAt: r.asked_at as string,
    askedBy: (r.asked_by as string) ?? null,
    askedByPersonId: r.asked_by_person_id as string,
    iAsked: r.i_asked === true,
    status: r.status as SupportQuestion['status'],
    replies: 0,
    answeredTheAsker: (r.answered_the_asker as boolean) ?? null,
    answeredTheAskerAt: (r.answered_the_asker_at as string) ?? null,
    turn: r.turn as SupportTurn,
    turnSince: r.turn_since as string,
    lastReplyBy: (r.last_reply_by as 'asker' | 'holder') ?? null,
    lastReplyAt: (r.last_reply_at as string) ?? null,
    assignedTo: (r.assigned_to as string) ?? null,
    assignedToPersonId: (r.assigned_to_person_id as string) ?? null,
    assignedAt: (r.assigned_at as string) ?? null,
    iAmAssignee: r.i_am_assignee === true,
    askerPriority: pri?.askerPriority ?? null,
    translationStatus: r.translation_status === 'machine' ? 'machine' : null,
    pageContext: (r.page_context as Partial<PageContext>) ?? null,
    pictures: ((r.attachments as { path: string; width: number; height: number }[] | null) ?? []).map((a) => ({
      path: a.path, width: a.width, height: a.height,
    })),
  }
}

/* ── 123: THE THREAD IN EACH READER'S LANGUAGE ──────────────────────────────
 *
 * The question, every reply and every team note are machine-translated into
 * the languages the THREAD's readers have (the asker, the lane, the assignee
 * — never the whole company), marked "Translated by One" with the original
 * one press away (111's line, 112's rule that One never reads it back).
 * The SERVER resolves for the reader and says `translationStatus`; this
 * file never picks a locale and never decides what to show.
 *
 * ASKING IS IDEMPOTENT AND CHEAP: publish-post's `translate_support` lists
 * only what is still missing, so a thread that needs nothing is one RPC and
 * no model call. The routes ask on every open (so the reader gets the
 * rendering even if the writer's request failed), after a reply and after a
 * note (so the OTHER side's is ready before they open), and the drawer asks
 * after a question is filed. A hand (119) or a move (121) can add a reader
 * in a new language; the next open asks again.
 */

/** Never throws — a rendering that does not arrive is a fallback, not an
 *  error the person reading can act on. Returns what was stored, as
 *  `subject:locale`, so a caller can re-read only when something changed. */
export async function requestSupportTranslation(questionId: string): Promise<string[]> {
  try {
    const { data, error } = await supabase.functions.invoke('publish-post', {
      body: { kind: 'translate_support', content_id: questionId },
    })
    if (error) return []
    return ((data as { translated?: string[] } | null)?.translated ?? [])
  } catch {
    return []
  }
}

/** The asker's own words behind a translated question — "Show original". */
export async function loadSupportQuestionOriginal(questionId: string): Promise<PostOriginal | null> {
  const { data, error } = await supabase.rpc('support_question_original', { p_content_id: questionId })
  if (error) throw new Error(error.message)
  const row = (data as { title: string | null; body: string | null; locale: string }[] | null)?.[0]
  if (!row) return null
  return { title: row.title ?? '', body: row.body ?? '', locale: row.locale }
}

/**
 * "Did this answer your question?" — THE ASKER, AND NOBODY ELSE.
 *
 * The server refuses every other caller including the lane holder (rule 21),
 * and that refusal is the point rather than a precaution: a holder marking
 * their own answer as resolving is how a queue looks clean while people are
 * still stuck, and it would be invisible, because a resolved thread is one
 * nobody looks at again.
 *
 * `false` does NOT close the thread and must not be presented as failure. It is
 * the only evidence this queue can produce that an answer did not land.
 *
 * NOT `rateOneAnswer`. That is 048's `wiki_feedback`, it means "the AI was
 * wrong", and it feeds `wiki_signals()` -> the #/wiki signals card. Mixing a
 * human thread's resolution into it would make one card answer two questions,
 * and the two disagree exactly where it matters (standing rule 11).
 */
export async function sayItAnswered(questionId: string, answered: boolean): Promise<void> {
  const { error } = await supabase.rpc('say_it_answered', {
    p_content_id: questionId,
    p_answered: answered,
  })
  if (error) throw new Error(error.message)
  refreshWaiting() // 325: a count on the nav or the bell may have moved
}

/* ── 174: THE LANE CLOSES A THREAD ────────────────────────────────────────────
 *
 * Whoever staffs the question (a holder of its lane, or the person it was
 * handed to) may close it. It is NOT "answered": that stays the asker's word
 * alone (090, rule 21). The asker is told and asked to confirm; anything said
 * afterwards reopens it — derived by `app.support_turn()`, so nothing here
 * reopens anything. Closing twice is once. A confirmed question refuses.
 */
export async function closeSupportQuestion(questionId: string): Promise<void> {
  const { error } = await supabase.rpc('close_support_question', { p_content_id: questionId })
  if (error) throw new Error(error.message)
  refreshWaiting() // 325: a count on the nav or the bell may have moved
}

/** Undo a lane close (the desk's Undo, or Reopen). Refuses what is not closed
 *  by the lane — a confirmed question is the asker's to reopen ("not yet"). */
export async function reopenSupportQuestion(questionId: string): Promise<void> {
  const { error } = await supabase.rpc('reopen_support_question', { p_content_id: questionId })
  if (error) throw new Error(error.message)
  refreshWaiting() // 325: a count on the nav or the bell may have moved
}
